import { GraduationCap, Calculator, PencilSimple, CaretRight } from "@phosphor-icons/react";
import { SectionShell } from "../../styleguide/Primitives";
import { Showcase } from "../../library/LibraryPrimitives";
import { Flow } from "../diagrams";
import { MasteryBadge } from "../../library/progress";
import { cn } from "@/lib/utils";

const MATHS = [
  { name: "Addition", level: "mastered" }, { name: "Subtraction", level: "mastered" },
  { name: "Multiplication", level: "strong" }, { name: "Division", level: "growing" },
  { name: "Fractions", level: "growing" }, { name: "Decimals", level: "seed" },
  { name: "Geometry", level: "seed" }, { name: "Word problems", level: "seed" },
];
const ENGLISH = [
  { name: "Reading comprehension", level: "strong" }, { name: "Grammar", level: "growing" },
  { name: "Vocabulary", level: "strong" }, { name: "Spelling", level: "growing" },
  { name: "Punctuation", level: "seed" },
];

const HIERARCHY = [
  { label: "Subject", desc: "Maths · English", tone: "blue" },
  { label: "Topic", desc: "e.g. Fractions", tone: "teal" },
  { label: "Lesson", desc: "A focused skill", tone: "orange" },
  { label: "Activity", desc: "Practice · story · puzzle", tone: "yellow" },
  { label: "Question", desc: "One small step", tone: "blue" },
];

function SubjectTree({ icon: Icon, title, tone, topics }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
      <div className="mb-4 flex items-center gap-3">
        <span className={cn("flex h-12 w-12 items-center justify-center rounded-2xl", tone === "blue" ? "bg-blue/12 text-blue-ink" : "bg-primary/12 text-primary-strong")}>
          <Icon size={24} weight="fill" />
        </span>
        <div>
          <p className="font-display text-xl font-extrabold">{title}</p>
          <p className="text-sm font-semibold text-muted-foreground">{topics.length} topics · ordered progression</p>
        </div>
      </div>
      <ol className="space-y-1.5">
        {topics.map((t, i) => (
          <li key={t.name} className="flex items-center gap-3 rounded-2xl bg-muted/40 px-3 py-2.5">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-card text-xs font-black text-muted-foreground shadow-soft">{i + 1}</span>
            <span className="flex-1 font-bold">{t.name}</span>
            <MasteryBadge level={t.level} />
          </li>
        ))}
      </ol>
    </div>
  );
}

export function LearnSection() {
  return (
    <SectionShell
      id="learn"
      eyebrow="Learn"
      eyebrowIcon={GraduationCap}
      title="Discover & practice, topic by topic"
      description="Learn organises Maths and English into ordered topics that unlock as mastery grows. Each topic is a journey of small lessons — never a long list to fear."
    >
      <div className="space-y-8">
        <div className="grid gap-5 lg:grid-cols-2">
          <SubjectTree icon={Calculator} title="Maths" tone="blue" topics={MATHS} />
          <SubjectTree icon={PencilSimple} title="English" tone="orange" topics={ENGLISH} />
        </div>

        <Showcase title="Topic hierarchy" description="From subject down to a single, gentle step" tone="muted">
          <Flow steps={HIERARCHY} />
        </Showcase>

        <div className="flex items-start gap-3 rounded-3xl border border-border bg-accent/50 p-5">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground"><CaretRight size={22} weight="bold" /></span>
          <p className="font-medium text-foreground">
            <strong>Progression:</strong> topics unlock in order; finishing lessons grows a topic from <strong className="text-muted-foreground">Seed</strong> → <strong className="text-teal-ink">Growing</strong> → <strong className="text-blue-ink">Strong</strong> → <strong className="text-primary-strong">Mastered</strong>. Science slots in as a third subject later, using the same structure.
          </p>
        </div>
      </div>
    </SectionShell>
  );
}

export default LearnSection;
